import { useParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import CompanyContainer from "../CompanyContainer";
import EditCompany from "../EditCompany";
import GHGProtocolModal from "../GHGProtocolModal";
// import YFSImportWizard from "../components/YFSImportWizard/YFSImportWizard";
import ImportDataModal from "./components/ImportDataModal/ImportDataModal";
import KlimakompassetImportLovable from "./components/ImportDataModal/KlimakompassetImportLovable";
import Sidebar from "../../CompanyDetail2/Sidebar";
import WaterComponent from "./Water";
import EnergyComponent from "./Energy";
import RegVehComponent from "./RegisteredVehicles";
import StationaryComponent from "./CombustionFossilFuels";
import EmissionGasComponent from "./EmissionGasses";
import TransportDeliveryComponent from "./TransportDelivery";
import InventoryComponent from "./Inventory";
import TravelComponent from "./Travel";
import CommuteComponent from "./EmployeeCommute";
import RemoteComponent from "./RemoteWork";
import SurplusResourceComponent from "./SurplusResources";
import "./index.css";
import ProcessingIntermediateComponent from "./ProcessingSold";
import UseOfSoldComponent from "./UseOfSold";
import FinalTreatmentComponent from "./FinalTreatment";
import YFSLabelCompanyDetail from "../../CompanyYFSLabel/YFSLabelCompanyDetail";
import GhgVerificationLovable from "./components/GhgVerificationLovable";
import { getCompanySummary } from "../../../apis/ARDash/CompanySummary";
import { useUserAccess } from "../../../components/WriteAccessContext";
import YearTab from "../../../components/YearTab";
import { getBusinessDetail } from "../../../apis/Business/Business";
import CompanyTabsBar from "../../Company/CompanyTabsBar";
import { upsertCompanyTab } from "../../Company/useCompanyOpenTabs";

const CompanyDetailPage = () => {
  let { businessId } = useParams();
  const [mainBusinessId, setMainBusinessId] = useState("");
  const [companyData, setCompanyData] = useState(null);
  const { isShare } = useUserAccess();
  const [showYearTab, setShowYearTab] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [yearData, setYearData] = useState("");
  const [yearTabHeight, setYearTabHeight] = useState(0);
  const yearTabRef = useRef(null);
  const [facilityName, setFacilityName] = useState("");
  const [showImportModal, setShowImportModal] = useState(false);
  const [showKlimakompassetImport, setShowKlimakompassetImport] = useState(false);

  useEffect(() => {
    setMainBusinessId(localStorage.getItem("id_business"));
  }, []);

  useEffect(() => {
    if (window.location.href.includes("company") && businessId) {
      getCompanySummary({
        id: businessId,
        setObjects: setCompanyData,
        isShared: isShare,
      });
    }
  }, [businessId, isShare]);

  useEffect(() => {
    if (!businessId) return;
    getBusinessDetail({ id: businessId, setName: setFacilityName });
  }, [businessId]);

  useEffect(() => {
    if (!businessId) return;
    if (!facilityName) return;
    upsertCompanyTab({
      id: businessId,
      name: facilityName,
      kind: "facility",
      to: `/company/${businessId}`,
    });
  }, [businessId, facilityName]);

  // Scroll detection to hide YearTab when scrolled past facility-container
  useEffect(() => {
    const handleScroll = () => {
      if (sidebarOpen) {
        // Don't update YearTab visibility when sidebar is open
        return;
      }

      const facilityElement = document.getElementById("facility-id");
      if (facilityElement) {
        const rect = facilityElement.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        // Hide YearTab when facility container is scrolled out of view
        // Check if the top of facility is above viewport or bottom is below viewport
        const isVisible = rect.top < windowHeight && rect.bottom > 0;
        setShowYearTab(isVisible);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Check initial position

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [sidebarOpen]);

  // Hide YearTab when sidebar opens, show when it closes
  useEffect(() => {
    if (sidebarOpen) {
      setShowYearTab(false);
    } else {
      // Re-check scroll position when sidebar closes
      const facilityElement = document.getElementById("facility-id");
      if (facilityElement) {
        const rect = facilityElement.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const isVisible = rect.top < windowHeight && rect.bottom > 0;
        setShowYearTab(isVisible);
      }
    }
  }, [sidebarOpen]);

  // Calculate YearTab height when it's visible
  useEffect(() => {
    if (showYearTab && !sidebarOpen) {
      // Use setTimeout to ensure DOM is updated
      const timer = setTimeout(() => {
        const yearTabElement = yearTabRef.current?.querySelector('.yearTab') || 
                              document.querySelector('.yearTab');
        if (yearTabElement) {
          const height = yearTabElement.offsetHeight;
          setYearTabHeight(height);
        }
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setYearTabHeight(0);
    }
  }, [showYearTab, sidebarOpen, yearData]);

  return (
    <>
      <Sidebar 
        isYearTabVisible={showYearTab && !sidebarOpen}
        yearTabHeight={yearTabHeight}
        onSidebarToggle={setSidebarOpen}
      />
      {showYearTab && !sidebarOpen && (
        <div ref={yearTabRef}>
          <YearTab setYearParent={setYearData} />
        </div>
      )}
      <CompanyTabsBar activeTabId={businessId} />
      <CompanyContainer>
        <EditCompany businessId={businessId} />
        <ImportDataModal
          open={showImportModal}
          onClose={() => setShowImportModal(false)}
          onChooseKlimakompasset={() => {
            setShowImportModal(false);
            setShowKlimakompassetImport(true);
          }}
          businessId={businessId}
        />
        <KlimakompassetImportLovable
          open={showKlimakompassetImport}
          onClose={() => setShowKlimakompassetImport(false)}
          businessId={businessId}
        />

        <div className="company-ghg-yfs">
          <div className="company-ghg-yfs__shell">
            <div className="company-ghg-yfs__intro">
              <p className="company-ghg-yfs__intro-kicker">The GHG Protocol</p>
              <h2 className="company-ghg-yfs__intro-title">Calculation flow</h2>
              <p className="company-ghg-yfs__intro-desc">
                Enter data for each flow below. Emissions are calculated
                automatically based on your inputs and emission factors.
              </p>
            </div>

            <div className="company-ghg-yfs__toolbar">
              <GHGProtocolModal />
              <button
                type="button"
                className="company-ghg-yfs__import-btn"
                onClick={() => setShowImportModal(true)}
              >
                Import data
              </button>
            </div>

            <div className="company-ghg-yfs__flows">
              <div className="company-ghg-yfs__flow-wrap energy-relative">
                <EnergyComponent
                  businessId={businessId}
                  mainBusinessId={mainBusinessId}
                />
                <div className="company-ghg-yfs__connector" aria-hidden="true" />
              </div>
              <div className="company-ghg-yfs__flow-wrap water-relative">
                <WaterComponent
                  businessId={businessId}
                  mainBusinessId={mainBusinessId}
                />
                <div className="company-ghg-yfs__connector" aria-hidden="true" />
              </div>
              <div className="company-ghg-yfs__flow-wrap regveh-relative">
                <RegVehComponent
                  businessId={businessId}
                  mainBusinessId={mainBusinessId}
                />
                <div className="company-ghg-yfs__connector" aria-hidden="true" />
              </div>
              <div className="company-ghg-yfs__flow-wrap stationary-relative">
                <StationaryComponent
                  businessId={businessId}
                  mainBusinessId={mainBusinessId}
                />
                <div className="company-ghg-yfs__connector" aria-hidden="true" />
              </div>
              <div className="company-ghg-yfs__flow-wrap emission-gas-relative">
                <EmissionGasComponent
                  businessId={businessId}
                  mainBusinessId={mainBusinessId}
                />
                <div className="company-ghg-yfs__connector" aria-hidden="true" />
              </div>
              <div className="company-ghg-yfs__flow-wrap transport-delivery-relative">
                <TransportDeliveryComponent
                  businessId={businessId}
                  mainBusinessId={mainBusinessId}
                />
                <div className="company-ghg-yfs__connector" aria-hidden="true" />
              </div>
              <div className="company-ghg-yfs__flow-wrap inventory-relative">
                <InventoryComponent
                  businessId={businessId}
                  mainBusinessId={mainBusinessId}
                />
                <div className="company-ghg-yfs__connector" aria-hidden="true" />
              </div>
              <div className="company-ghg-yfs__flow-wrap travel-relative">
                <TravelComponent
                  businessId={businessId}
                  mainBusinessId={mainBusinessId}
                />
                <div className="company-ghg-yfs__connector" aria-hidden="true" />
              </div>
              <div className="company-ghg-yfs__flow-wrap commute-relative">
                <CommuteComponent
                  businessId={businessId}
                  mainBusinessId={mainBusinessId}
                />
                <div className="company-ghg-yfs__connector" aria-hidden="true" />
              </div>
              <div className="company-ghg-yfs__flow-wrap remote-relative">
                <RemoteComponent
                  businessId={businessId}
                  mainBusinessId={mainBusinessId}
                />
                <div className="company-ghg-yfs__connector" aria-hidden="true" />
              </div>
              <div className="company-ghg-yfs__flow-wrap surplus-relative">
                <SurplusResourceComponent
                  businessId={businessId}
                  mainBusinessId={mainBusinessId}
                />
                <div className="company-ghg-yfs__connector" aria-hidden="true" />
              </div>
              <div className="company-ghg-yfs__flow-wrap processing-sold-relative">
                <ProcessingIntermediateComponent
                  businessId={businessId}
                  mainBusinessId={mainBusinessId}
                />
                <div className="company-ghg-yfs__connector" aria-hidden="true" />
              </div>
              <div className="company-ghg-yfs__flow-wrap use-sold-relative">
                <UseOfSoldComponent
                  businessId={businessId}
                  mainBusinessId={mainBusinessId}
                />
                <div className="company-ghg-yfs__connector" aria-hidden="true" />
              </div>
              <div className="company-ghg-yfs__flow-wrap final-treatment-relative">
                <FinalTreatmentComponent
                  businessId={businessId}
                  mainBusinessId={mainBusinessId}
                />
              </div>
            </div>

            <GhgVerificationLovable
              companyData={companyData}
              facilityName={facilityName}
              yearData={yearData}
            />
            {companyData && (
              <YFSLabelCompanyDetail companyData={companyData} />
            )}
          </div>
        </div>
      </CompanyContainer>
    </>
  );
};

export default CompanyDetailPage;
